// SPDX-License-Identifier: AGPL-3.0-or-later

export type HermesModelPersistence = 'session' | 'global';

export interface HermesModelProvider {
	name: string;
	slug: string;
	models: ReadonlyArray<string>;
}

export interface HermesModelCatalog {
	providers: ReadonlyArray<HermesModelProvider>;
	efforts: ReadonlyArray<string>;
}

export interface HermesModelPickerSelection {
	provider: string;
	model: string;
	effort: string;
	persistence: HermesModelPersistence;
}

/** Offline safety net only. Hermes' generated catalog is authoritative. */
export const FALLBACK_PROVIDERS: ReadonlyArray<HermesModelProvider> = [
	{
		name: 'OpenRouter',
		slug: 'openrouter',
		models: ['deepseek/deepseek-v4-flash-0731'],
	},
	{name: 'FreeLLMAPI (Multi-Account Pool)', slug: 'freellmapi', models: ['auto']},
];

export const HERMES_MODEL_EFFORTS: ReadonlyArray<string> = [
	'none',
	'minimal',
	'low',
	'medium',
	'high',
	'xhigh',
	'max',
	'ultra',
];

export const FALLBACK_MODEL_CATALOG: HermesModelCatalog = {
	providers: FALLBACK_PROVIDERS,
	efforts: HERMES_MODEL_EFFORTS,
};

// Keep this endpoint literal: the self-hosted production build does not expose
// an import.meta.env object at runtime.
// Public names-only catalog generated from the current Hermes inventory.
// The picker requests it uncached on every open; a scheduled publisher keeps
// this file live without exposing Hermes API credentials through Fluxer.
const MODEL_CATALOG_ENDPOINT = 'https://fluxer.simtail.de/assets/hermes-model-catalog.json';
const MODEL_CATALOG_CACHE_KEY = 'fluxer.hermes.model-catalog.v2';
const MAX_CATALOG_PROVIDERS = 128;
const MAX_CATALOG_MODELS = 512;
const MAX_MODEL_NAME_LENGTH = 256;
const MAX_EFFORTS = 32;
let catalogPromise: Promise<HermesModelCatalog> | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function parseEfforts(input: unknown): Array<string> {
	if (!Array.isArray(input)) return [...HERMES_MODEL_EFFORTS];
	const efforts: Array<string> = [];
	const seen = new Set<string>();
	for (const rawEffort of input.slice(0, MAX_EFFORTS)) {
		if (typeof rawEffort !== 'string') continue;
		const effort = rawEffort.trim().slice(0, 64);
		if (effort && !seen.has(effort.toLowerCase())) {
			seen.add(effort.toLowerCase());
			efforts.push(effort);
		}
	}
	return efforts.length ? efforts : [...HERMES_MODEL_EFFORTS];
}

/** Validate and normalize Hermes' public names-only catalog. */
export function parseHermesModelCatalog(input: unknown): HermesModelCatalog | null {
	if (!isRecord(input) || !Array.isArray(input.providers)) return null;
	const providers: Array<HermesModelProvider> = [];
	const seenProviders = new Set<string>();
	for (const rawProvider of input.providers.slice(0, MAX_CATALOG_PROVIDERS)) {
		if (!isRecord(rawProvider) || typeof rawProvider.name !== 'string' || typeof rawProvider.slug !== 'string')
			continue;
		const name = rawProvider.name.trim().slice(0, MAX_MODEL_NAME_LENGTH);
		const slug = rawProvider.slug.trim().slice(0, MAX_MODEL_NAME_LENGTH);
		if (!name || !slug || seenProviders.has(slug.toLowerCase()) || !Array.isArray(rawProvider.models)) continue;
		const models: Array<string> = [];
		const seenModels = new Set<string>();
		for (const rawModel of rawProvider.models.slice(0, MAX_CATALOG_MODELS)) {
			if (typeof rawModel !== 'string') continue;
			const model = rawModel.trim().slice(0, MAX_MODEL_NAME_LENGTH);
			if (model && !seenModels.has(model.toLowerCase())) {
				seenModels.add(model.toLowerCase());
				models.push(model);
			}
		}
		if (!models.length) continue;
		seenProviders.add(slug.toLowerCase());
		providers.push({name, slug, models});
	}
	return {providers, efforts: parseEfforts(input.efforts)};
}

function readCachedCatalog(): HermesModelCatalog | null {
	try {
		const raw = window.localStorage.getItem(MODEL_CATALOG_CACHE_KEY);
		if (!raw) return null;
		return parseHermesModelCatalog(JSON.parse(raw));
	} catch {
		return null;
	}
}

function writeCachedCatalog(catalog: HermesModelCatalog): void {
	try {
		window.localStorage.setItem(MODEL_CATALOG_CACHE_KEY, JSON.stringify({version: 2, ...catalog}));
	} catch {
		// Private browsing/storage-disabled environments still use the live result.
	}
}

/** Fetch the current catalog on every picker open; cache only covers outages. */
export function loadHermesModelCatalog(signal?: AbortSignal, forceRefresh = true): Promise<HermesModelCatalog> {
	if (catalogPromise == null || forceRefresh) {
		catalogPromise = fetch(`${MODEL_CATALOG_ENDPOINT}?refresh=${forceRefresh ? '1' : '0'}`, {
			method: 'GET',
			credentials: 'omit',
			cache: 'no-store',
			signal,
		})
			.then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
			.then((payload) => {
				const parsed = parseHermesModelCatalog(payload);
				if (parsed == null) throw new Error('Invalid Hermes model catalog');
				writeCachedCatalog(parsed);
				return parsed;
			})
			.catch(() => readCachedCatalog() ?? FALLBACK_MODEL_CATALOG);
	}
	return catalogPromise;
}

export function buildHermesModelPickerCommands(selection: HermesModelPickerSelection): Array<string> {
	const scope = selection.persistence === 'global' ? '--global' : '--session';
	const reasoningScope = selection.persistence === 'global' ? ' --global' : '';
	return [
		`/model ${selection.model} --provider ${selection.provider} ${scope}`,
		`/reasoning ${selection.effort}${reasoningScope}`,
	];
}

export function providerLabel(provider: HermesModelProvider): string {
	return `${provider.name} · ${provider.models.length} Modelle`;
}

export const MAX_VISIBLE_PICKER_ITEMS = 5;
